import {useDataContext} from "../SurveyContextProvider";
import {useIntl} from "react-intl";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Popover} from "../../UIElements";
import {getInstanceConfigValue} from "../../utils";
import {GetWhatsappTemplateTranslation} from "../../api/APISurvey";
import {AlertCircle, AlertTriangle, Info} from "lucide-react";
import {ReactComponent as ReminderIcon} from "../../svg/reminder.svg";
import {ReactComponent as ReminderIconSmall} from "../../svg/reminder-s.svg";
import {ReactComponent as OptionsIcon} from "../../svg/options.svg";
import {ReactComponent as DeleteIcon} from "../../svg/delete.svg";
import {CompositeDecorator, ContentState, Editor, EditorState, Modifier} from "draft-js";
import {AddVariable, LengthIndicator, VariableComponent, variableStrategy} from "../SurveyMessages";
import {EditorFocusContext} from "../SurveyView";
import {useLanguageData} from "../../App";
import "./SurveyReminders.scss"
import {SimpleVariableComponent} from "./messagesTab/WhatsappMessage";

export function SurveyReminders() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: instanceConfig } = useDataContext('instanceConfig')
    const { instanceLang } = useLanguageData()
    const surveyLanguage = surveyData?.config?.language || instanceLang
    const channelValue = getInstanceConfigValue("contact", "channel", instanceConfig);
    const whatsAppInstance = Array.isArray(channelValue)
        ? channelValue.some(channel => channel.toLowerCase().includes("whatsapp"))
        : channelValue?.toLowerCase()?.includes("whatsapp");
    const whatsAppTemplate = getInstanceConfigValue("whatsapp", "templateReminder", instanceConfig)
    const getWhatsappTemplateTranslation = GetWhatsappTemplateTranslation(whatsAppTemplate, whatsAppInstance)
    const surveyFinished = surveyData.status === "FINISHED"
    const intl = useIntl()

    if (whatsAppInstance && (!whatsAppTemplate || getWhatsappTemplateTranslation.isError)) {
        return <div className={"whatsapp-reminder error message-content"}>
            <AlertTriangle/>
            {intl.formatMessage({
                id:'whatsapp.reminder.missing',
                defaultMessage: 'The WhatsApp reminder is not configured correctly. Please contact the Annie team for assistance, if you wish to use reminders.'
            })}
        </div>
    }

    if (whatsAppInstance && (getWhatsappTemplateTranslation.isLoading)) {
        return <div className={"whatsapp-reminder loading message-content"}>
            {intl.formatMessage({
                id:'whatsapp.reminder.loading',
                defaultMessage: 'Loading the WhatsApp reminder message...'
            })}
        </div>
    }

    // Get template data if WhatsApp instance
    let templateContent = null
    if (whatsAppInstance && getWhatsappTemplateTranslation.isSuccess) {
        const templates = getWhatsappTemplateTranslation?.data
        const template = templates?.find(obj => obj?.language === surveyLanguage)

        if (!template) {
            return <div className={"whatsapp-reminder error message-content"}>
                <Info />
                {intl.formatMessage({
                    id:'whatsapp.config.translation.missing',
                    defaultMessage: 'The WhatsApp message template not in the selected survey language. Please change the survey language or contact the Annie team for assistance, if you wish to use reminders.'
                })}
            </div>
        }

        templateContent = template?.value
    }

    let reminders
    if (surveyData.config.hasOwnProperty('reminders')) {
        reminders = surveyData.config["reminders"]
    } else {
        reminders = []
    }

    const addReminder = () => {
        const reminderDelay = reminders?.length < 1 ? 24 : parseInt(reminders[reminders?.length - 1]?.delay) + parseInt(reminders[0]?.delay)
        let newReminder = {
            delay: reminderDelay,
            message: templateContent || ""
        }
        reminders.push(newReminder)
        const newSurveyData = {...surveyData}
        newSurveyData.config["reminders"] = reminders
        setSurveyData(newSurveyData)
    }

    return <>
        <div className={"reminders"}>
            <h4>
                {intl.formatMessage(
                    {
                        id: 'survey.navigation.reminders',
                        defaultMessage: 'Reminders',
                    })}
            </h4>
            {reminders.length < 1 &&
                <p className={"placeholder"}>
                    {intl.formatMessage(
                        {
                            id: 'survey.reminderMissing.placeholder',
                            defaultMessage: 'Your survey doesn\'t have any reminders. Start adding by clicking the button below.',
                        })}
                </p>
            }
            {reminders.map((reminder, i) => {
                return <Reminder
                    reminder={reminder}
                    i={i}
                    key={i}
                    whatsAppInstance={whatsAppInstance}
                    templateContent={templateContent}
                />
            })}
            {surveyFinished ?
                <button className={"add-new-reminder add-new-button"} disabled>
                    {intl.formatMessage(
                        {
                            id: 'survey.reminders.addReminder',
                            defaultMessage: '+ Add reminder',
                        })}
                </button>
                :
                <button className={"add-new-reminder add-new-button"} onClick={addReminder}>
                    {intl.formatMessage(
                        {
                            id: 'survey.reminders.addReminder',
                            defaultMessage: '+ Add reminder',
                        })}
                </button>
            }
        </div>
    </>
}

function Reminder({ reminder, i, whatsAppInstance, templateContent }) {
    const [editorIsActive, setEditorIsActive] = useState(false)
    const editorContainer = useRef(null)
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')

    const parsedMessage = useMemo(() => {
        if (!reminder?.message) return null

        // Split the message into parts, keeping the variable delimiters
        const parts = reminder.message.split(/({{[^}]+}})/g)

        return parts.map((part, index) => {
            // Check if this part is a variable
            const variableMatch = part.match(/^{{(\w+)}}$/)

            if (variableMatch) {
                const variable = variableMatch[1]
                return (
                    <SimpleVariableComponent
                        key={index}
                        variable={variable}
                    />
                )
            }

            // Regular text
            return <span key={index}>{part}</span>
        })
    }, [reminder?.message])

    // Update message if it differs from template in WhatsApp instances
    useEffect(() => {
        if (whatsAppInstance && templateContent && reminder.message !== templateContent) {
            const reminders = surveyData.config["reminders"]
            const updatedReminders = [...reminders]
            updatedReminders[i] = { ...reminder, message: templateContent }

            const newSurveyData = {...surveyData}
            newSurveyData.config["reminders"] = updatedReminders
            setSurveyData(newSurveyData)
        }
    }, [whatsAppInstance, templateContent, reminder.message, i, surveyData, setSurveyData])

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (editorContainer.current && !editorContainer.current.contains(event.target) ) {
                setEditorIsActive(false)
            }
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    },[editorContainer])

    const handleEditorClick = () => {
        setEditorIsActive(true);
    }

    return <div className={"message reminder"}>
        <div className={"message-icon"}>
            <ReminderIcon />
        </div>
        <div className={"message-content"}>
            {
                whatsAppInstance ?
                    <div className={"whatsapp-reminder-content"}>
                        <div>{parsedMessage}</div>
                        <ReminderPopup i={i} />
                    </div> :
                    <div>
                        <div ref={editorContainer} onClick={handleEditorClick}>
                            <EditableContent text={reminder.message} i={i} editorIsActive={editorIsActive} />
                        </div>
                        <ReminderPopup i={i} />
                    </div>
            }
            <ReminderTools i={i} />
        </div>
    </div>
}

function ReminderTools({i}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const reminderTime = surveyData.config["reminders"][i].delay
    const intl = useIntl()

    const handleInputChange = e => {
        const newSurveyData = {...surveyData}
        let value = e.target.value;

        if (value === '') {
            newSurveyData.config["reminders"][i].delay = value
        } else {
            value = Number(value)
            if (!isNaN(value)) {
                newSurveyData.config["reminders"][i].delay = value
            }
        }
        setSurveyData(newSurveyData)
    };

    const handleInputBlur = () => {
        if (reminderTime === '' || isNaN(reminderTime)) {
            const newSurveyData = {...surveyData}
            newSurveyData.config["reminders"][i].delay = 24 // Default value
            setSurveyData(newSurveyData)
        }
    };

    return <div className={"reminder-tools"}>
        <ReminderIconSmall />
        {surveyFinished ?
            <input type={"number"} value={reminderTime}
                   disabled/> :
            <input
                type={"number"}
                value={reminderTime === 0 ? '' : reminderTime}
                placeholder="24"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
            />
        }
        {intl.formatMessage(
            {
                id: 'survey.reminders.hours',
                defaultMessage: 'hours',
            })}
    </div>
}

function ReminderPopup({i}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const [popoverOpen, setPopoverOpen] = useState(false)
    const intl = useIntl()
    const deleteReminder = () => {
        const newSurveyData = {...surveyData}
        newSurveyData.config["reminders"].splice(i, 1)
        setSurveyData(newSurveyData)
        setPopoverOpen(false)
    }
    if (surveyFinished) {
        return null
    }
    return <div className={"options-popover popover-container"}>
        <div className={"popover-toggle"} onClick={() => setPopoverOpen(true)}>
            <OptionsIcon />
        </div>
        {popoverOpen &&
            <Popover closePopover={() => setPopoverOpen(false)}>
                <button className={"delete"} onClick={deleteReminder}>
                    <DeleteIcon />
                    {intl.formatMessage(
                        {
                            id: 'survey.message.deleteReminder',
                            defaultMessage: 'Delete Reminder',
                        })}
                </button>
            </Popover>
        }
    </div>
}

function EditableContent(props) {
    const text = props?.text
    const editorIsActive = props?.editorIsActive
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { setFocus } = useContext(EditorFocusContext)
    const surveyEditable = surveyData.status === "DRAFT" || surveyData.status === "SCHEDULED"
    const [editorState, setEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromText(props.text)),
    )
    const intl = useIntl()

    //Set variable component and update editor state with it onload
    const decorators = new CompositeDecorator([
        {
            strategy: variableStrategy,
            component: VariableComponent,
            editable: false,
            props: {
                editorState: editorState,
                editorUpdate: e => editorUpdate(e)
            }
        }])
    useEffect(()=>{
        setEditorState(EditorState.set(editorState, {decorator: decorators}))
    },[])

    // Update editor state if content changed elsewhere eg. undo
    useEffect(()=> {
        if (editorState.getCurrentContent().getPlainText() !== text && !editorIsActive) {
            const newContent = ContentState.createFromText(text)
            const newState = EditorState.push(editorState, newContent, 'insert-characters')
            const stateWithDecorators = EditorState.set(newState, {decorator: decorators})
            // Clear editors undo stack
            const clearedHistoryState = EditorState.set(stateWithDecorators, {
                undoStack: EditorState.createEmpty().getUndoStack()
            })

            setEditorState(clearedHistoryState)
        }
    },[text])

    // Instead of debounce, just save every 5 seconds to global state to not overflow it with every update
    useEffect(() => {
        const saveInterval = setInterval(() => {
            if (editorIsActive) saveToContext()
        }, 5000)

        // Clean up timer
        return () => {
            clearInterval(saveInterval)
        }
    }, [editorState])

    const saveToContext = () => {
        const plainText = editorState.getCurrentContent().getPlainText()
        const newSurveyData = {...surveyData}

        newSurveyData.config["reminders"][props.i].message = plainText

        setSurveyData(newSurveyData)
    }

    const editorUpdate = e => {
        // update editor with variable decorator
        const editorStateWithDecorators = EditorState.set(e, {decorator: decorators})
        setEditorState(editorStateWithDecorators)
    }

    const handleBlur = () => {
        setFocus('reminder'+props?.i, false)
        saveToContext()
    }

    const pasteVariable = variable => {
        const newState = pasteText(variable, editorState)
        editorUpdate(newState)
    }

    const pasteText = (text, editorValue) => {
        const selection = editorValue.getSelection()
        const contentState = editorValue.getCurrentContent()
        const newContent = Modifier.insertText(contentState, selection, text)
        const newEditorState = EditorState.push(editorValue, newContent, 'insert-characters')
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter())
    }

    const handleUndo = () => {
        const undoStack = editorState.getUndoStack()

        if (undoStack.size !== 0) {
            const newEditorState = EditorState.undo(editorState)
            setEditorState(newEditorState)
        }
    }

    const handleKeyCommand = (command) => {
        if (command === 'undo') {
            handleUndo()
            return 'handled'
        }

        // Handle other key commands or let Draft.js handle them
        return 'not-handled'
    }

    const placeholder = intl.formatMessage(
        {
            id: 'survey.reminder.placeholder',
            defaultMessage: 'Click to type your reminder',
        })

    return <div className={"editor"}>
        {surveyEditable ?
            <>
                <Editor
                    editorState={editorState}
                    onChange={editorUpdate}
                    placeholder={placeholder}
                    stripPastedStyles={true}
                    onFocus={() => setFocus('reminder'+props?.i, true)}
                    onBlur={handleBlur}
                    handleKeyCommand={handleKeyCommand}
                />
                {props.editorIsActive &&
                    <div className={"editor-toolbar"}>
                        <AddVariable pasteVariable={variable => pasteVariable(variable)}/>
                        <LengthIndicator content={editorState.getCurrentContent().getPlainText()}/>
                    </div>
                }
            </> :
            <Editor editorState={editorState} readOnly={true} disabled stripPastedStyles={true} placeholder={placeholder} />
        }
    </div>
}